import {Component, HostListener, OnInit} from '@angular/core';
import {NgClass, NgForOf, NgIf} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {ChatDialog, Message} from '../../shared/models/chat.model';
import {DropdownComponent} from "../../shared/components/ui/dropdown/dropdown.component";
import {DropdownItemComponent} from "../../shared/components/ui/dropdown/dropdown-item/dropdown-item.component";
import {PopupComponent} from "../../shared/components/popup/popup.component";
import {ButtonComponent} from "../../shared/components/ui/button/button.component";
import {InputFieldComponent} from "../../shared/components/form/input/input-field.component";
import {ChatService} from "../../shared/services/chat.service";
import { MarkdownPipe } from '../../shared/pipe/markdown.pipe';
import { SafeHtmlPipe } from '../../shared/pipe/safe-html.pipe';

@Component({
  selector: 'app-chat',
  imports: [
    NgClass,
    FormsModule,
    NgForOf,
    NgIf,
    DropdownComponent,
    DropdownItemComponent,
    PopupComponent,
    ButtonComponent,
    InputFieldComponent,
    // Pipes
    MarkdownPipe,
    SafeHtmlPipe
  ],
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.css']
})
export class ChatComponent implements OnInit {
  dialogs: ChatDialog[] = [];

  selectedDialogId: number | null = null;
  openMenuId: number | null = null;

  renameDialogId: number | null = null;
  showRenamePopup: boolean = false;
  renameDialogTitle: string = '';

  messages: Message[] = [
    { sender: 'ai', text: 'Hi! How can I help you today?', timestamp: new Date() }
  ];

  // Models dropdown state
  installedModels: string[] = [];
  selectedModel: string | null = null;
  modelDropdownOpen = false;

  newMessage: string = '';
  homeMode = true;
  isLoading = false;

  constructor(
      private chatService: ChatService
  ) { }

  ngOnInit(): void {
    this.chatService.getInstalledModels().subscribe({
      next: (models) => {
        this.installedModels = models || [];
        // Try to fetch default model and select it if available
        this.chatService.getDefaultModel().subscribe({
          next: (def) => {
            const defaultModel = (def || '').trim();
            if (defaultModel && this.installedModels.includes(defaultModel)) {
              this.selectedModel = defaultModel;
            } else if (this.installedModels.length && !this.selectedModel) {
              this.selectedModel = this.installedModels[0];
            }
          },
          error: () => {
            // On error, fallback to first model if none selected
            if (this.installedModels.length && !this.selectedModel) {
              this.selectedModel = this.installedModels[0];
            }
          }
        });
      },
      error: (err) => {
        console.error('Failed to load models', err);
        this.installedModels = [];
      }
    });
  }

  selectDialog(id: number) {
    this.homeMode = false;
    this.selectedDialogId = id;
    // fetch from backend
    this.messages = [
      { sender: 'ai', text: 'This is chat #' + id, timestamp: new Date() }
    ];
  }

  addNewChat() {
    const newId = this.dialogs.length + 1;
    const newChat = { id: newId, title: `Chat #${newId}`, lastMessage: '' };
    this.dialogs.unshift(newChat);
    this.selectDialog(newChat.id);
  }

  toggleMenu(id: number, event: MouseEvent) {
    event.stopPropagation();
    this.openMenuId = this.openMenuId === id ? null : id;
  }

  deleteDialog(id: number) {
    this.dialogs = this.dialogs.filter(d => d.id !== id);
    if (this.selectedDialogId === id) {
      this.selectedDialogId = null;
      this.homeMode = true;
      this.messages = [{ sender: 'ai', text: 'Hi! How can I help you today?', timestamp: new Date() }];
    }
    this.openMenuId = null;
  }

  openRenamePopup(id: number) {
    const dialog = this.dialogs.find(d => d.id === id);
    if (!dialog) return;
    this.renameDialogId = id;
    this.renameDialogTitle = dialog.title;
    this.showRenamePopup = true;
    this.openMenuId = null;
  }

  handleRenameSave(newTitle: string) {
    if (this.renameDialogId !== null) {
      const dialog = this.dialogs.find(d => d.id === this.renameDialogId);
      if (dialog) dialog.title = newTitle;
    }
    this.showRenamePopup = false;
  }

  handleRenameCancel() {
    this.showRenamePopup = false;
  }

  renameDialog(id: number) {
    const newTitle = prompt('Enter new name:');
    if (newTitle) {
      const dialog = this.dialogs.find(d => d.id === id);
      if (dialog) dialog.title = newTitle;
    }
    this.openMenuId = null;
  }

  closeDropdown() {
    this.openMenuId = null;
    this.modelDropdownOpen = false;
  }

  toggleModelDropdown(event: MouseEvent) {
    event.stopPropagation();
    this.modelDropdownOpen = !this.modelDropdownOpen;
  }

  selectModel(model: string) {
    this.selectedModel = model;
    this.modelDropdownOpen = false;
  }

  sendMessage() {
    if (!this.newMessage.trim()) return;

    if (this.homeMode) {
      const newId = this.dialogs.length + 1;
      const newChat = { id: newId, title: `Chat #${newId}`, lastMessage: '' };
      this.dialogs.unshift(newChat);
      this.selectedDialogId = newChat.id;
      this.homeMode = false;
      this.messages = [];
    }

    const userMessage = this.newMessage;
    this.messages.push({
      sender: 'user' as const,
      text: userMessage,
      timestamp: new Date()
    });

    const currentDialog = this.dialogs.find(d => d.id === this.selectedDialogId);
    if (currentDialog) currentDialog.lastMessage = userMessage;

    this.newMessage = '';

    // Create an initial AI message
    const aiMessage = {
      sender: 'ai' as const,
      text: '',
      timestamp: new Date()
    };
    this.messages.push(aiMessage);

    this.isLoading = true;

    // call backend via SSE
    this.chatService.streamChat(userMessage, this.selectedModel ?? undefined).subscribe({
      next: (chunk) => {
        aiMessage.text += chunk;
        if (currentDialog) currentDialog.lastMessage = aiMessage.text;
      },
      error: (err) => {
        console.error('SSE error', err);
        aiMessage.text = 'Error connecting to AI service.';
        this.isLoading = false;
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }
}
